import { HealthCardHelper } from "@/Common/Helpers/HealthCareHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { IPatientIdentifiersModel } from "@/Common/Models/Patient/UI/Profile/IPatientIdentifiersModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileIdentifiersHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_AddHealthCard, this.IdentifiersAddHealthCard);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_AddOtherId, this.IdentifiersAddOtherId, true);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_RemoveHealthCard, this.IdentifiersRemoveHealthCard);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_RemoveOtherId, this.IdentifiersRemoveOtherId);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_NAMSelectCountry, this.IdentifiersNAMSelectCountry);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_NAMSelectProvince, this.IdentifiersNAMSelectProvince);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_NAMNumberChange, this.IdentifiersNAMNumberChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_NAMVersionChange, this.IdentifiersNAMVersionChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_NAMExpirationDateChange, this.IdentifiersNAMExpirationDateChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_Identifier01Change, this.IdentifiersIdentifier01Change);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_Identifier02Change, this.IdentifiersIdentifier02Change);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_Identifier03Change, this.IdentifiersIdentifier03Change);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_OtherIdOrganizationChange, this.IdentifiersOtherIdOrganizationChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_OtherIdNumberChange, this.IdentifiersOtherIdNumberChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_CancelClick, this.IdentifiersCancelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_SaveClick, this.IdentifiersSaveClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Identifiers_EditClick, this.IdentifiersEditClick);
    }

    public IdentifiersEditClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];

        if (idModel.get("HealthCards").count() == 0)
            idModel = this.AddEmptyHealthCardToModel(idModel);

        if (idModel.get("OtherIdentifiers").count() == 0) {
            idModel = this.AddEmptyOtherIdToModel(idModel);
        }

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);

        var modelToUndo: IPatientIdentifiersModel = store.PatientProfileIdentifiersModels[request.PatientId];
        modelToUndo = modelToUndo.set("PatientContactId", modelToUndo.get("PatientContactId")); // to get copy/clone of immutable model
        store.PatientProfileIdentifiersUndoModels[request.PatientId] = modelToUndo;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    public IdentifiersCancelClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientUIModels[request.PatientId];
        var undoModel = store.PatientProfileIdentifiersUndoModels[request.PatientId];
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientProfileIdentifiersModels[request.PatientId] = undoModel;
        delete store.PatientProfileIdentifiersUndoModels[request.PatientId];
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersSaveClick(request: LocalEvent, store: PatientStore): void {
        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];

        // check for empty healthcards - don't save them
        var healthcardsToKeep: PatientIdentifier[] = [];
        idModel.get("HealthCards").toArray().forEach(x => {
            if ((x.Number && x.Number.trim().length > 0) || (x.Version && x.Version.trim().length > 0))
                healthcardsToKeep.push(x);
        });
        idModel = idModel.set("HealthCards", Immutable.List(healthcardsToKeep));

        // check for empty otherIds - don't save them
        var otherIdsToKeep: PatientIdentifier[] = [];
        idModel.get("OtherIdentifiers").toArray().forEach(x => {
            if ((x.Number && x.Number.trim().length > 0) || (x.Name && x.Name.trim().length > 0))
                otherIdsToKeep.push(x);
        });
        idModel = idModel.set("OtherIdentifiers", Immutable.List(otherIdsToKeep));

        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
        store.PatientUIModels[request.PatientId] = uiModel;
        let idModels: IPatientIdentifiersModel = store.PatientProfileIdentifiersModels[request.PatientId];
        store.ActionsProfile.savePatientIdentifiers(idModels);
    }

    private IdentifiersAddHealthCard(request: LocalEvent, store: PatientStore): void {
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        idModel = this.AddEmptyHealthCardToModel(idModel);
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
    }

    private IdentifiersRemoveHealthCard(request: LocalEvent, store: PatientStore): void {
        var idToremove = request.Get<string>("value");

        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var currentHealthCards = idModel.get("HealthCards").toArray();
        currentHealthCards = currentHealthCards.filter(x => x.HealthIdentification_id !== idToremove);
        idModel = idModel.set("HealthCards", Immutable.List(currentHealthCards));

        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
    }

    private IdentifiersAddOtherId(request: LocalEvent, store: PatientStore): void {
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        idModel = this.AddEmptyOtherIdToModel(idModel);
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
    }

    private IdentifiersRemoveOtherId(request: LocalEvent, store: PatientStore): void {
        var idToremove = request.Get<string>("value");
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var currendIds = idModel.get("OtherIdentifiers").toArray();
        currendIds = currendIds.filter(x => x.HealthIdentification_id !== idToremove);
        idModel = idModel.set("OtherIdentifiers", Immutable.List(currendIds));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
    }

    private IdentifiersNAMSelectCountry(request: LocalEvent, store: PatientStore): void {
        var selectedCountryId = request.Get<string>("value");
        var selectedCountry = cacheStore.GetLookupById(selectedCountryId);
        var healthCard = request.Get<PatientIdentifier>("item");

        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var patientModel = store.PatientModels[request.PatientId];

        var currentNAMs: PatientIdentifier[] = idModel.get("HealthCards").toArray();

        for (var i = 0; i < currentNAMs.length; i++) {
            var x = currentNAMs[i];
            if (x.HealthIdentification_id === healthCard.HealthIdentification_id) {
                var clone: PatientIdentifier = MyleHelper.CloneObject(x);
                clone.CountryLookupId = selectedCountry.LookupID;
                clone.AvailableCountryRegionLookups = cacheStore.GetCountryRegionLookups(selectedCountry);
                clone.ProvinceLookupId = (clone.AvailableCountryRegionLookups && clone.AvailableCountryRegionLookups.length > 0) ? clone.AvailableCountryRegionLookups[0].LookupID : clone.CountryLookupId;
                clone.ExpiryDate = HealthCardHelper.updateExpirationDate(clone.ProvinceLookupId, clone.ExpiryDate);
                clone.IsValid = HealthCardHelper.isHealthCardValid(
                    clone.Number,
                    clone.ProvinceLookupId,
                    clone.CountryLookupId,
                    patientModel.IndividualResponse.FirstName,
                    patientModel.IndividualResponse.LastName,
                    patientModel.IndividualResponse.DateOfBirth,
                    patientModel.IndividualResponse.GenderLookupId
                );

                currentNAMs[i] = clone;
            }
        }

        idModel = idModel.set("HealthCards", Immutable.List(currentNAMs));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
    }

    private IdentifiersNAMSelectProvince(request: LocalEvent, store: PatientStore): void {
        var selectedProvinceId = request.Get<string>("value");
        var healthCard = request.Get<PatientIdentifier>("item");

        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var patientModel = store.PatientModels[request.PatientId];

        var currentNAMs: PatientIdentifier[] = idModel.get("HealthCards").toArray();

        for (var i = 0; i < currentNAMs.length; i++) {
            var x = currentNAMs[i];
            if (x.HealthIdentification_id === healthCard.HealthIdentification_id) {
                var clone: PatientIdentifier = MyleHelper.CloneObject(x);
                clone.ProvinceLookupId = selectedProvinceId;
                clone.ExpiryDate = HealthCardHelper.updateExpirationDate(selectedProvinceId, clone.ExpiryDate);
                clone.IsValid = HealthCardHelper.isHealthCardValid(
                    clone.Number,
                    clone.ProvinceLookupId,
                    clone.CountryLookupId,
                    patientModel.IndividualResponse.FirstName,
                    patientModel.IndividualResponse.LastName,
                    patientModel.IndividualResponse.DateOfBirth,
                    patientModel.IndividualResponse.GenderLookupId
                );
                currentNAMs[i] = clone;
            }
        }

        idModel = idModel.set("HealthCards", Immutable.List(currentNAMs));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
    }

    private IdentifiersNAMNumberChange(request: LocalEvent, store: PatientStore): void {
        var newValue = request.Get<string>("value");
        var healthCard = request.Get<PatientIdentifier>("item");

        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var patientModel = store.PatientModels[request.PatientId];

        var currentNAMs: PatientIdentifier[] = idModel.get("HealthCards").toArray();

        for (var i = 0; i < currentNAMs.length; i++) {
            var x = currentNAMs[i];
            if (x.HealthIdentification_id === healthCard.HealthIdentification_id) {
                var clone: PatientIdentifier = MyleHelper.CloneObject(x);
                clone.Number = newValue;
                clone.IsValid = HealthCardHelper.isHealthCardValid(
                    clone.Number,
                    clone.ProvinceLookupId,
                    clone.CountryLookupId,
                    patientModel.IndividualResponse.FirstName,
                    patientModel.IndividualResponse.LastName,
                    patientModel.IndividualResponse.DateOfBirth,
                    patientModel.IndividualResponse.GenderLookupId
                );
                currentNAMs[i] = clone;
            }
        }

        idModel = idModel.set("HealthCards", Immutable.List(currentNAMs));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersNAMVersionChange(request: LocalEvent, store: PatientStore): void {
        var version = request.Get<string>("value");
        var healthCard = request.Get<PatientIdentifier>("item");

        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var currentNAMs: PatientIdentifier[] = idModel.get("HealthCards").toArray();

        for (var i = 0; i < currentNAMs.length; i++) {
            var x = currentNAMs[i];
            if (x.HealthIdentification_id === healthCard.HealthIdentification_id) {
                var clone: PatientIdentifier = MyleHelper.CloneObject(x);
                clone.Version = version;
                currentNAMs[i] = clone;
            }
        }

        idModel = idModel.set("HealthCards", Immutable.List(currentNAMs));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersNAMExpirationDateChange(request: LocalEvent, store: PatientStore): void {
        const expirationDate = request.Get<Date>("value");
        const identifier = request.Get<PatientIdentifier>("item");

        const uiModel = store.PatientUIModels[request.PatientId];
        let idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        const healthCards: PatientIdentifier[] = idModel.get("HealthCards").toArray();
        const healthCard: PatientIdentifier = healthCards.find(h => h.HealthIdentification_id === identifier.HealthIdentification_id);
        healthCard.ExpiryDate = HealthCardHelper.updateExpirationDate(identifier.ProvinceLookupId, expirationDate);

        idModel = idModel.set("HealthCards", Immutable.List(healthCards));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersIdentifier01Change(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        idModel = idModel.set("Identifier01", val);
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersIdentifier02Change(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        idModel = idModel.set("Identifier02", val);
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersIdentifier03Change(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        idModel = idModel.set("Identifier03", val);
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersOtherIdOrganizationChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var otherId = request.Get<PatientIdentifier>("item");

        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var currentOtherIds: PatientIdentifier[] = idModel.get("OtherIdentifiers").toArray();

        for (var i = 0; i < currentOtherIds.length; i++) {
            var x = currentOtherIds[i];
            if (x.HealthIdentification_id === otherId.HealthIdentification_id) {
                var clone: PatientIdentifier = MyleHelper.CloneObject(x);
                clone.Name = val;
                currentOtherIds[i] = clone;
            }
        }

        idModel = idModel.set("OtherIdentifiers", Immutable.List(currentOtherIds));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private IdentifiersOtherIdNumberChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var otherId = request.Get<PatientIdentifier>("item");

        var uiModel = store.PatientUIModels[request.PatientId];
        var idModel = store.PatientProfileIdentifiersModels[request.PatientId];
        var currentOtherIds: PatientIdentifier[] = idModel.get("OtherIdentifiers").toArray();

        for (var i = 0; i < currentOtherIds.length; i++) {
            var x = currentOtherIds[i];
            if (x.HealthIdentification_id === otherId.HealthIdentification_id) {
                var clone: PatientIdentifier = MyleHelper.CloneObject(x);
                clone.Number = val;
                currentOtherIds[i] = clone;
            }
        }

        idModel = idModel.set("OtherIdentifiers", Immutable.List(currentOtherIds));
        store.PatientProfileIdentifiersModels[request.PatientId] = idModel;

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private AddEmptyHealthCardToModel(idModel: IPatientIdentifiersModel): IPatientIdentifiersModel {
        const currentHealthCards = idModel.get("HealthCards").toArray();
        const healthCard = HealthCardHelper.createEmptyHealthCard();

        currentHealthCards.push(healthCard);
        idModel = idModel.set("HealthCards", Immutable.List(currentHealthCards));

        return idModel;
    }

    private AddEmptyOtherIdToModel(idModel: IPatientIdentifiersModel): IPatientIdentifiersModel {
        const currentIds = idModel.get("OtherIdentifiers").toArray();
        const id = HealthCardHelper.createEmptyOtherIdentifier();
        currentIds.push(id);
        return idModel.set("OtherIdentifiers", Immutable.List(currentIds));
    }
}

export { PatientProfileIdentifiersHandler };
